import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { NotificationService } from '../services/notificationService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationContextProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const currentUserId = currentUser?.uid || null;
  const currentUserRole = userProfile?.role || null;

  const loadNotifications = useCallback(async () => {
    if (!currentUserId) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userNotifications = await NotificationService.getUserNotifications(currentUserId, 50);
      
      // Filtrer les notifications selon le rôle de l'utilisateur
      let filteredNotifications = userNotifications;
      if (currentUserRole === 'reseller') {
        // Pour les revendeurs, ne garder que les notifications de transfert/recharge de solde.
        filteredNotifications = userNotifications.filter(
          (notification) => notification.type === 'balance_credited' || notification.type === 'balance_debited'
        );
      }
      
      setNotifications(filteredNotifications);
      setUnreadCount(filteredNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, currentUserRole]);

  // Charger les notifications quand l'utilisateur connecté ou son rôle change.
  useEffect(() => {
    if (currentUserId && currentUserRole) {
      loadNotifications();
    }
  }, [currentUserId, currentUserRole, loadNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await NotificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur lors du marquage comme lu:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!currentUserId) return;
    
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(notification => 
          NotificationService.markAsRead(notification.id)
        )
      );
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
    }
  }, [currentUserId, notifications]);

  const addNotification = useCallback(async (type, title, message, data = {}) => {
    if (!currentUserId) return;
    
    try {
      await NotificationService.createNotification(
        currentUserId,
        type,
        title,
        message,
        data
      );
      
      // Recharger les notifications pour avoir la version complète
      await loadNotifications();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la notification:', error);
    }
  }, [currentUserId, loadNotifications]);

  const removeNotification = useCallback((notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, [notifications]);

  // Recharger les notifications périodiquement
  useEffect(() => {
    if (!currentUserId || !currentUserRole) return;

    const interval = setInterval(() => {
      loadNotifications();
    }, 30000); // Recharger toutes les 30 secondes

    return () => clearInterval(interval);
  }, [currentUserId, currentUserRole, loadNotifications]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification
  }), [
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
